import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, Users, Phone, User, MapPin, Download,
  CheckCircle, Clock, CreditCard, Filter, Calendar, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SearchPassengersPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [checkedInPassengers, setCheckedInPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [filterMode, setFilterMode] = useState('today'); // 'today', 'trip', 'search'
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchTrips();
      fetchCheckedInPassengers();
    }
  }, [token]);

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API_URL}/trips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data || []);
    } catch (error) {
      console.error('Failed to fetch trips:', error);
    }
  };

  const fetchCheckedInPassengers = async (tripId = null) => {
    setLoading(true);
    try {
      let url = `${API_URL}/passengers/checked-in`;
      if (tripId) {
        url += `?trip_id=${tripId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCheckedInPassengers(response.data.passengers || []);
      setFilterMode(tripId ? 'trip' : 'today');
    } catch (error) {
      console.error('Failed to fetch checked-in passengers:', error);
      setCheckedInPassengers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTripFilter = (tripId) => {
    setSelectedTrip(tripId);
    if (tripId === 'all') {
      fetchCheckedInPassengers();
    } else {
      fetchCheckedInPassengers(tripId);
    }
    setSearched(false);
    setResults([]);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    setSearched(true);
    setFilterMode('search');
    
    try {
      const response = await axios.post(
        `${API_URL}/bookings/search?query=${encodeURIComponent(searchTerm)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(response.data.results || []);
      
      if (response.data.count === 0) {
        toast.info('No passengers found matching your search');
      }
    } catch (error) {
      console.error('Search failed:', error);
      // Fallback to local filtering
      const bookingsRes = await axios.get(`${API_URL}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = bookingsRes.data.filter(booking => {
        const searchLower = searchTerm.toLowerCase();
        const matchesRef = booking.booking_ref?.toLowerCase().includes(searchLower);
        const matchesPassenger = booking.passengers?.some(p => 
          p.full_name?.toLowerCase().includes(searchLower) ||
          p.phone?.includes(searchTerm) ||
          p.id_number?.toLowerCase().includes(searchLower)
        );
        const matchesSeat = booking.seat_numbers?.some(s => 
          s.toLowerCase().includes(searchLower)
        );
        
        return matchesRef || matchesPassenger || matchesSeat;
      });

      setResults(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      let url = `${API_URL}/passengers/checked-in/export`;
      if (selectedTrip && selectedTrip !== 'all') {
        url += `?trip_id=${selectedTrip}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download CSV file
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported ${response.data.count} passengers to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export passengers');
    } finally {
      setExporting(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearched(false);
    setResults([]);
    setFilterMode('today');
    fetchCheckedInPassengers();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="search-passengers-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Search Passengers
            </h1>
            <p className="text-slate-600">
              Find passengers by name, phone, ID, seat, or booking reference
            </p>
          </div>
          
          <Button 
            onClick={handleExportCSV}
            disabled={exporting || checkedInPassengers.length === 0}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export Checked-In CSV
          </Button>
        </div>

        {/* Search & Filter */}
        <Card className="card-panashe">
          <CardContent className="p-6 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Enter name, phone, ID, seat number, or booking ref..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="passenger-search-input"
                />
              </div>
              <Button 
                onClick={handleSearch}
                disabled={loading}
                className="btn-panashe"
                data-testid="search-passengers-btn"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
              {searched && (
                <Button variant="outline" onClick={clearSearch}>
                  Clear
                </Button>
              )}
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600">Filter by trip:</span>
              </div>
              <Select value={selectedTrip} onValueChange={handleTripFilter}>
                <SelectTrigger className="w-[300px]" data-testid="trip-filter-select">
                  <SelectValue placeholder="Today's checked-in passengers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Today's Trips</SelectItem>
                  {trips.map(trip => (
                    <SelectItem key={trip.id} value={trip.id}>
                      {trip.trip_number} - {trip.origin} → {trip.destination} ({format(parseISO(trip.departure_datetime), 'HH:mm')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">Search: Name</Badge>
              <Badge variant="outline" className="text-xs">Phone</Badge>
              <Badge variant="outline" className="text-xs">ID/Passport</Badge>
              <Badge variant="outline" className="text-xs">Seat</Badge>
              <Badge variant="outline" className="text-xs">Booking Ref</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Checked-In Passengers (Default View) */}
        {!searched && (
          <Card className="card-panashe">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Checked-In Passengers
                <Badge className="bg-green-100 text-green-700">{checkedInPassengers.length}</Badge>
                {filterMode === 'today' && <Badge variant="outline">Today</Badge>}
                {filterMode === 'trip' && <Badge variant="outline">Selected Trip</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-panashe-red" />
                </div>
              ) : checkedInPassengers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p className="font-medium">No checked-in passengers</p>
                  <p className="text-sm">Passengers will appear here once checked in</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ID/Passport</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Seat</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Trip</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Route</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Check-In</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Boarded</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {checkedInPassengers.map((passenger, idx) => (
                        <tr key={idx} className="hover:bg-slate-50" data-testid={`checked-in-row-${idx}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium">{passenger.full_name}</p>
                            <p className="text-xs text-slate-500">{passenger.booking_ref}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{passenger.phone}</td>
                          <td className="px-4 py-3 text-sm font-mono">{passenger.id_number}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{passenger.seat}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm">{passenger.trip_number}</td>
                          <td className="px-4 py-3 text-sm">{passenger.route}</td>
                          <td className="px-4 py-3 text-sm">
                            {passenger.check_in_time && format(parseISO(passenger.check_in_time), 'HH:mm')}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={(passenger.boarded || passenger.check_in_time) ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {(passenger.boarded || passenger.check_in_time) ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search Results */}
        {searched && (
          <Card className="card-panashe">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Results
                <Badge variant="outline">{results.length} found</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p className="font-medium">No passengers found</p>
                  <p className="text-sm">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {results.map((booking, idx) => (
                    <div 
                      key={idx}
                      className="border border-slate-200 rounded-lg overflow-hidden"
                      data-testid={`search-result-${idx}`}
                    >
                      <div className="bg-slate-50 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-panashe-blue rounded-lg flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="font-mono font-bold text-panashe-blue">
                              {booking.booking_ref}
                            </p>
                            <p className="text-sm text-slate-500">{booking.route}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge className={
                            booking.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }>
                            <CreditCard className="h-3 w-3 mr-1" />
                            {booking.payment_status}
                          </Badge>
                          
                          <Badge className={
                            booking.checked_in 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-700'
                          }>
                            {booking.checked_in ? (
                              <><CheckCircle className="h-3 w-3 mr-1" />Checked In</>
                            ) : (
                              <><Clock className="h-3 w-3 mr-1" />Pending</>
                            )}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {booking.passengers?.map((passenger, pIdx) => (
                            <div 
                              key={pIdx}
                              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                            >
                              <div className="w-10 h-10 bg-panashe-gold/20 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-panashe-gold" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{passenger.full_name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Phone className="h-3 w-3" />
                                  {passenger.phone}
                                </div>
                                <p className="text-xs text-slate-400">
                                  {passenger.id_number || passenger.passport_number}
                                </p>
                              </div>
                              <Badge variant="outline" className="font-mono">
                                {booking.seat_numbers?.[pIdx]}
                              </Badge>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-sm">
                          <div className="text-slate-500">
                            <Calendar className="h-4 w-4 inline mr-1" />
                            {booking.trip_date && format(parseISO(booking.trip_date), 'PPP HH:mm')}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/dashboard/scanner?ref=${booking.booking_ref}`)}
                          >
                            Check In
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SearchPassengersPage;
